/*
 * An environment's rail, as data (docs/ux-principles.md, "One frame everywhere"): its sections in menu order, their
 * clusters, the parts of the ones that are groups (Security, App, Feedback, Settings), and which a site and a person
 * get. Kept apart from the (big) Environments page so the command palette and the breadcrumbs can name the places.
 *
 * Addresses: /sites/:id/env/:slug/:section/:part, a group's first part without its own segment (…/settings is
 * Visibility, …/feedback the comments); a number after feedback is one comment (…/feedback/12).
 */
import { allows } from "../../../api/access";
import type { EnvironmentSummary, SiteDetail } from "../../../api/types";
import { envPartLabel } from "../env";

// App: a Node.js app's processes, variables, routes, jobs and logs (pages/sites/node/); only on those sites.
// PHP: its version, limits, workers, OPcache, errors and more settings (env/PhpPart.tsx); WordPress and PHP sites.
// Move: a part of its own while the environment moves to another server (pages/moves/MovePart.tsx).
// Database: its databases, users, tables, export and import, and the login (env/DatabasePart.tsx); sites with one.
export const ENV_SECTIONS = ["code", "app", "php", "database", "data", "status", "traffic", "logs", "backups", "security", "domains", "access", "feedback", "settings", "move"] as const;
export type EnvSection = (typeof ENV_SECTIONS)[number];
export const SECTION_LABEL: Record<EnvSection, string> = { code: "Code", app: "App", php: "PHP", database: "Database", status: "Status", traffic: "Traffic", logs: "Logs", backups: "Backups", data: "Database & files", access: "SSH & SFTP", security: "Security", domains: "Domains & HTTPS", feedback: "Feedback", settings: "Settings", move: "Move" };

/**
 * The rail's clusters, each under a small heading: what runs and what it holds; watching it; keeping it safe;
 * reaching it; the people reviewing it and its settings.
 */
const CLUSTER: Record<EnvSection, string> = { code: "Build", app: "Build", php: "Build", database: "Build", data: "Build", status: "Monitor", traffic: "Monitor", logs: "Monitor", backups: "Protect", security: "Protect", domains: "Connect", access: "Connect", feedback: "Manage", settings: "Manage", move: "Manage" };
export const clusterOf = (x: EnvSection) => CLUSTER[x];

export const SETTINGS_PARTS = ["visibility", "caching", "general"] as const;
export type SettingsPart = (typeof SETTINGS_PARTS)[number];
export const SETTINGS_PART_LABEL: Record<SettingsPart, string> = { visibility: "Visibility", caching: "Caching", general: "General" };
export const isSettingsPart = (p: string | undefined | null): p is SettingsPart => (SETTINGS_PARTS as readonly string[]).includes(p ?? "");

export const FEEDBACK_PARTS = ["comments", "setup", "options"] as const;
export type FeedbackPart = (typeof FEEDBACK_PARTS)[number];
export const FEEDBACK_PART_LABEL: Record<FeedbackPart, string> = { comments: "Comments", setup: "Setup", options: "Options" };
export const isFeedbackPart = (p: string | undefined | null): p is FeedbackPart => (FEEDBACK_PARTS as readonly string[]).includes(p ?? "");
/** A comment's number in a Feedback address (…/feedback/12), or null. */
export const commentNumberOf = (p: string | undefined | null): number | null => (p && /^\d+$/.test(p) ? Number(p) : null);

/** The part a group's own address shows; its address leaves the part out. */
export const DEFAULT_PART: Partial<Record<EnvSection, string>> = { security: "overview", app: "processes", settings: "visibility", feedback: "comments" };

/** WordPress and PHP app sites run PHP (a static site and a Node.js app don't). */
export const runsPhp = (site: Pick<SiteDetail, "kind">) => site.kind !== "static" && site.kind !== "node";

/** WordPress and the app types have a database (docs/app-types.md); static sites, Node.js apps and plain PHP ones don't. */
export const hasDatabase = (site: Pick<SiteDetail, "kind" | "appType">) => site.kind === "wordpress" || (site.kind === "php" && !!site.appType);

/** A section's name in the rail: a static or Node.js site's Files and SSH & SFTP; a Node.js app's web logs (its own are under App). */
export const sectionLabel = (x: EnvSection, kind: SiteDetail["kind"] | undefined): string => (x === "access" || x === "data" ? envPartLabel(x, kind) : x === "logs" && kind === "node" ? "Web logs" : SECTION_LABEL[x]);

/** Website feedback is for Dev, Test and extra environments (docs/feedback.md), once the API says how it's set. */
export const hasFeedbackPart = (env: EnvironmentSummary) => env.name !== "live" && env.feedback !== undefined;

/** A static site deployed from git: its files are its code, which moves between environments by deploying, not copying. */
export const copiesByDeploy = (site: Pick<SiteDetail, "kind">, env: EnvironmentSummary) => site.kind === "static" && env.code.layout === "releases";

/**
 * The sections a person gets for this environment, in menu order. SSH only for people who manage it, as every
 * credential (the API refuses everyone else); a static or Node.js site has no database; a Node.js app and a static
 * site from git have nothing a copy carries (their code arrives by deploy), so no Files part. `current` keeps a part reached by its address.
 */
export function envSections(site: Pick<SiteDetail, "kind" | "appType">, env: EnvironmentSummary, current?: EnvSection): EnvSection[] {
  return ENV_SECTIONS.filter(
    (x) =>
      (x !== "access" || allows(env.access, "manage")) &&
      (x !== "logs" || allows(env.access, "deploy") || current === "logs") &&
      (x !== "move" || !!env.move || current === "move") &&
      (x !== "app" || site.kind === "node") &&
      (x !== "php" || runsPhp(site)) &&
      (x !== "database" || hasDatabase(site) || current === "database") &&
      (x !== "data" || (site.kind !== "node" && !copiesByDeploy(site, env)) || current === "data") &&
      (x !== "feedback" || hasFeedbackPart(env)),
  );
}

/**
 * Settings' parts for this environment. Visibility always; Caching where there's a cache to set (a Node.js app's
 * browser caching is in App › Routes); General when it holds something: loading uploads from Live, an extra
 * environment's branch and backups, the server it runs on (`server`: the person may move it), deleting it.
 */
export function settingsParts(site: Pick<SiteDetail, "kind">, env: EnvironmentSummary, server: boolean): SettingsPart[] {
  const live = env.name === "live";
  const general = env.name === "extra" || (site.kind === "wordpress" && !live && env.uploadsFromLive != null) || server || (!live && allows(env.access, "manage") && env.status !== "deleting");
  return SETTINGS_PARTS.filter((p) => (p !== "caching" || site.kind !== "node") && (p !== "general" || general));
}

/** Feedback's parts: its options once it's on. */
export const feedbackParts = (env: EnvironmentSummary): FeedbackPart[] => FEEDBACK_PARTS.filter((p) => p !== "options" || !!env.feedback?.on);

/** A place's address under the site: env/dev/settings/caching (a group's first part leaves its segment out). */
export const envPath = (slug: string, section: EnvSection, part?: string) => `env/${slug}/${section}${part && DEFAULT_PART[section] && part !== DEFAULT_PART[section] ? `/${part}` : ""}`;

/** Words people may search for a place by, besides its name. */
const PLACE_WORDS: Partial<Record<string, string>> = {
  code: "deploy commits git releases rollback",
  php: "php version limits opcache workers errors",
  database: "database mysql mariadb tables users export import sql adminer phpmyadmin password",
  data: "copy database files",
  status: "health uptime response time",
  traffic: "visitors analytics pages",
  logs: "access error logs",
  backups: "backup restore",
  domains: "domains https certificate ssl addresses dns cdn",
  access: "ssh sftp credentials keys",
  move: "move server",
  "settings/visibility": "password lock maintenance search engines noindex hide",
  "settings/caching": "cache page cache object cache cdn browser",
  "settings/general": "delete server move uploads branch",
  "feedback/comments": "comments review inbox",
  "feedback/setup": "comment button review links turn on",
  "feedback/options": "emails notifications snapshots reviewers",
};

export type EnvPlace = { key: string; label: string; group: EnvSection | null; path: string; words: string };

/**
 * Every place in an environment, for the command palette: its sections, and the parts of Security, the App, Feedback
 * and Settings. `parts` names a group's parts ("Firewall"); the palette says whose.
 */
export function envPlaces(site: Pick<SiteDetail, "kind" | "appType">, env: EnvironmentSummary, slug: string, server: boolean, parts: { security: { id: string; label: string }[]; app: { id: string; label: string }[] }): EnvPlace[] {
  const out: EnvPlace[] = [];
  for (const x of envSections(site, env)) {
    const label = sectionLabel(x, site.kind);
    const group: { id: string; label: string }[] =
      x === "security" ? parts.security : x === "app" ? parts.app : x === "settings" ? settingsParts(site, env, server).map((p) => ({ id: p, label: SETTINGS_PART_LABEL[p] })) : x === "feedback" ? feedbackParts(env).map((p) => ({ id: p, label: FEEDBACK_PART_LABEL[p] })) : [];
    // A group is its parts (its own address is its first part's): "Visibility", under Dev's settings.
    if (!group.length) out.push({ key: x, label, group: null, path: envPath(slug, x), words: `${label} ${PLACE_WORDS[x] ?? ""}` });
    for (const p of group) out.push({ key: `${x}/${p.id}`, label: p.label, group: x, path: envPath(slug, x, p.id), words: `${label} ${p.label} ${PLACE_WORDS[`${x}/${p.id}`] ?? ""}` });
  }
  return out;
}
